import React, { useEffect, useState } from "react"
import supabase from "../supabaseconfig"
import { useBrukerContext } from "../BrukerContext"
import style from "./informasjon.module.css"

type InformasjonData = {
    kalender_url: string | null
    leie_epostliste: string | null
}

export default function Informasjon() {
    const { bruker } = useBrukerContext()
    const [info, setInfo] = useState<InformasjonData | null>(null)
    const [fraDato, setFraDato] = useState("")
    const [tilDato, setTilDato] = useState("")
    const [visMailValg, setVisMailValg] = useState(false)
    const [etasje, setEtasje] = useState<"oppe" | "begge" | null>(null)
    const [visDatoFeil, setVisDatoFeil] = useState(false)

    const PRISER = {
        oppe: { leie: 35, vask: 100 },
        begge: { leie: 42, vask: 150 },
    }

    function beregnNetter() {
        if (!datoerValgt) return 0
        const fra = new Date(fraDato)
        const til = new Date(tilDato)
        return Math.round(
            (til.getTime() - fra.getTime()) / (1000 * 60 * 60 * 24),
        )
    }

    useEffect(() => {
        supabase()
            .from("informasjon")
            .select("kalender_url, leie_epostliste")
            .single()
            .then(({ data, error }) => {
                if (error)
                    console.error("Feil ved henting av informasjon:", error)
                setInfo(data)
            })
    }, [])

    const datoerValgt = fraDato !== "" && tilDato !== ""

    function formaterDato(dato: string) {
        return new Date(dato).toLocaleDateString("nb-NO", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
    }

    function gcalDato(dato: string) {
        return dato.replace(/-/g, "")
    }

    function gcalDatoEksklusiv(dato: string) {
        const d = new Date(dato)
        d.setDate(d.getDate() + 1)
        return d.toISOString().slice(0, 10).replace(/-/g, "")
    }

    function lagMailParams() {
        const mottakere = info?.leie_epostliste ?? ""
        const emne = encodeURIComponent("Forespørsel om leie av Residencen")
        const datotekst = datoerValgt
            ? `${formaterDato(fraDato)} til ${formaterDato(tilDato)}`
            : "[fra dato] til [til dato]"
        const etasjetekst =
            etasje === "oppe" ? "kun oppe etasje" : "begge etasjer"
        const netter = beregnNetter()
        const priser = PRISER[etasje]
        const leietotal = netter * priser.leie
        const total = leietotal + priser.vask
        const pristekst = `${netter} netter × ${priser.leie} € + vask ${priser.vask} € = ${total} €`
        const kropp = encodeURIComponent(
            `Hei,\n\nJeg ønsker å leie Residencen i perioden ${datotekst} (${etasjetekst}). Perioden er markert med rød i kalenderen.\n\nEstimert pris: ${pristekst}\n\nMvh,\n${bruker ?? ""}`,
        )
        return { mottakere, emne, kropp }
    }

    function åpneIGoogleKalender() {
        const tittel = encodeURIComponent(`${bruker} i Spania forespørsel`)
        const datoer = `${gcalDato(fraDato)}/${gcalDatoEksklusiv(tilDato)}`

        let kalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${tittel}&dates=${datoer}`

        if (info?.kalender_url) {
            try {
                const kalenderId = new URL(info.kalender_url).searchParams.get(
                    "src",
                )
                if (kalenderId)
                    kalUrl += `&src=${encodeURIComponent(kalenderId)}`
            } catch {
                // ugyldig URL, ignorer
            }
        }

        window.open(kalUrl, "_blank")
    }

    function åpneIGmail() {
        const { mottakere, emne, kropp } = lagMailParams()
        setVisMailValg(false)
        window.open(
            `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(mottakere)}&su=${emne}&body=${kropp}`,
            "_blank",
        )
    }

    function åpneIMailKlient() {
        const { mottakere, emne, kropp } = lagMailParams()
        setVisMailValg(false)
        window.location.href = `mailto:${mottakere}?subject=${emne}&body=${kropp}`
    }

    return (
        <div>
            <div id="leie" className="page">
                <h1 className="topHeader">LEIE</h1>
            </div>
            <div className="underPage">
                <div className={style.wrapper}>
                    <div className={style.seksjon}>
                        <p style={{ marginBottom: "1rem" }}>
                            Forespørsel om leie av Residencen burde sendes i god
                            tid, men maks 12 måneder, før planlagt reise. Ukene
                            i sommerskoleferien er som regel tildelt Siv, Viggo
                            eller Mikkel på forhånd. Sjekk gjerne av det før du
                            sender forespørsel.
                        </p>

                        <p className={style.label}>Priser</p>
                        <div className={style.prisOppsummering}>
                            <div className={style.prisOppsummeringRad}>
                                <span>Kun oppe</span>
                                <span>35 € / natt · vask 100 €</span>
                            </div>
                            <div className={style.prisOppsummeringRad}>
                                <span>Begge etasjer</span>
                                <span>42 € / natt · vask 150 €</span>
                            </div>
                            <p className={style.prisOppsummeringNotat}>
                                OBS: Legg gjerne igjen litt ekstra ved høyt
                                strømforbruk.
                            </p>
                        </div>
                        <p>
                            Godt renhold er også godt vedlikehold og skal
                            utføres av innleid hjelp. Man kan{" "}
                            <strong>ikke</strong> velge bort vask.{" "}
                        </p>
                        <p>
                            Legg vaskepenger i marmorskrinet i spisestuen. De
                            øvrige penger for leie legges i skrin på toppen av
                            vitrineskapet.
                        </p>

                        <div style={{ marginBottom: "1.75rem" }} />
                        <p>
                            <span className={style.label}>
                                For å leie må du:
                            </span>
                        </p>
                        <ol>
                            <li>
                                <strong>Finn ledig tid i kalenderen</strong>
                                {info?.kalender_url && (
                                    <div className={style.kalenderWrapper}>
                                        <iframe
                                            title="Residencen kalender"
                                            src={info.kalender_url}
                                            frameBorder="0"
                                            scrolling="no"
                                            className={style.kalender}
                                        />
                                        <p className={style.notat}>
                                            Farger på hendelser vises ikke her.
                                            Se direkte i{" "}
                                            <a
                                                className="lenkeknapp"
                                                href="https://calendar.google.com/calendar/u/0/r/month?pli=1"
                                            >
                                                Google Calendar
                                            </a>{" "}
                                            hvis du vil se farger.
                                        </p>
                                    </div>
                                )}
                            </li>
                            <li>
                                <strong>
                                    Legg inn perioden du ønsker med rød farge
                                </strong>
                                {info?.leie_epostliste && (
                                    <div className={style.liInnhold}>
                                        <p className={style.advarsel}>
                                            Husk å sette fargen til rød (går
                                            dessverre ikke an å sette
                                            automatisk)
                                        </p>
                                        <div className={style.datoRad}>
                                            <div className={style.datoGruppe}>
                                                <label
                                                    htmlFor="fra-dato"
                                                    className={style.datoLabel}
                                                >
                                                    Fra
                                                </label>
                                                <input
                                                    id="fra-dato"
                                                    type="date"
                                                    value={fraDato}
                                                    min={new Date()
                                                        .toISOString()
                                                        .slice(0, 10)}
                                                    onChange={(e) => {
                                                        const v = e.target.value
                                                        setFraDato(v)
                                                        if (v && tilDato)
                                                            setVisDatoFeil(
                                                                false,
                                                            )
                                                    }}
                                                    className={`${style.datoInput}${visDatoFeil && !fraDato ? " " + style.datoInputFeil : ""}`}
                                                />
                                            </div>
                                            <div className={style.datoGruppe}>
                                                <label
                                                    htmlFor="til-dato"
                                                    className={style.datoLabel}
                                                >
                                                    Til
                                                </label>
                                                <input
                                                    id="til-dato"
                                                    type="date"
                                                    value={tilDato}
                                                    min={
                                                        fraDato ||
                                                        new Date()
                                                            .toISOString()
                                                            .slice(0, 10)
                                                    }
                                                    onChange={(e) => {
                                                        const v = e.target.value
                                                        setTilDato(v)
                                                        if (fraDato && v)
                                                            setVisDatoFeil(
                                                                false,
                                                            )
                                                    }}
                                                    className={`${style.datoInput}${visDatoFeil && !tilDato ? " " + style.datoInputFeil : ""}`}
                                                />
                                            </div>
                                        </div>
                                        {datoerValgt && (
                                            <button
                                                type="button"
                                                onClick={åpneIGoogleKalender}
                                                style={{
                                                    alignSelf: "flex-start",
                                                }}
                                            >
                                                Legg til i kalender ↗
                                            </button>
                                        )}
                                    </div>
                                )}
                            </li>
                            <li>
                                <strong>
                                    Send en forespørsel til Viggo, Siv og Mikkel
                                </strong>
                                {info?.leie_epostliste && (
                                    <div className={style.liInnhold}>
                                        <p className={style.datoLabel}>
                                            Hvilke etasjer?
                                        </p>
                                        <div className={style.etasjeToggle}>
                                            {(["oppe", "begge"] as const).map(
                                                (valg) => (
                                                    <label
                                                        key={valg}
                                                        className={`${style.etasjeKort} ${etasje === valg ? style.etasjeKortAktiv : ""}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="etasje"
                                                            value={valg}
                                                            checked={
                                                                etasje === valg
                                                            }
                                                            onChange={() => {
                                                                setEtasje(valg)
                                                                if (
                                                                    !datoerValgt
                                                                ) {
                                                                    setVisDatoFeil(
                                                                        true,
                                                                    )
                                                                    document
                                                                        .getElementById(
                                                                            !fraDato
                                                                                ? "fra-dato"
                                                                                : "til-dato",
                                                                        )
                                                                        ?.focus()
                                                                }
                                                            }}
                                                            className={
                                                                style.etasjeRadio
                                                            }
                                                        />
                                                        <span
                                                            className={
                                                                style.etasjeRadioIndikator
                                                            }
                                                        />
                                                        <span>
                                                            {valg === "oppe"
                                                                ? "Kun oppe"
                                                                : "Begge etasjer"}
                                                        </span>
                                                    </label>
                                                ),
                                            )}
                                        </div>
                                        {!datoerValgt && (
                                            <p className={style.prisHint}>
                                                Fyll ut datoene over for å sende
                                                forespørsel med ferdig mal.
                                            </p>
                                        )}
                                        {datoerValgt &&
                                            etasje &&
                                            (() => {
                                                const netter = beregnNetter()
                                                const priser = PRISER[etasje]
                                                const leietotal =
                                                    netter * priser.leie
                                                const total =
                                                    leietotal + priser.vask
                                                return (
                                                    <div
                                                        className={
                                                            style.prisboks
                                                        }
                                                    >
                                                        <p
                                                            className={
                                                                style.prisboksTittel
                                                            }
                                                        >
                                                            Estimert pris
                                                        </p>
                                                        <div
                                                            className={
                                                                style.prisrad
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    style.prislinje
                                                                }
                                                            >
                                                                <span>
                                                                    {netter}{" "}
                                                                    netter ×{" "}
                                                                    {
                                                                        priser.leie
                                                                    }{" "}
                                                                    €
                                                                </span>
                                                                <span>
                                                                    {leietotal}{" "}
                                                                    €
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={
                                                                    style.prislinje
                                                                }
                                                            >
                                                                <span>
                                                                    Vask
                                                                </span>
                                                                <span>
                                                                    {
                                                                        priser.vask
                                                                    }{" "}
                                                                    €
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={`${style.prislinje} ${style.prisTotal}`}
                                                            >
                                                                <span>
                                                                    Totalt
                                                                </span>
                                                                <span>
                                                                    {total} €
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })()}
                                        {datoerValgt && etasje && (
                                            <div className={style.mailVelger}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setVisMailValg(
                                                            (v) => !v,
                                                        )
                                                    }
                                                >
                                                    Send forespørsel ↗
                                                </button>
                                                {visMailValg && (
                                                    <>
                                                        <div
                                                            className={
                                                                style.popoverBakgrunn
                                                            }
                                                            onClick={() =>
                                                                setVisMailValg(
                                                                    false,
                                                                )
                                                            }
                                                        />
                                                        <div
                                                            className={
                                                                style.popover
                                                            }
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    åpneIGmail
                                                                }
                                                            >
                                                                Åpne i Gmail
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    åpneIMailKlient
                                                                }
                                                            >
                                                                Åpne i
                                                                e-postklient
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                            <li>
                                <strong>
                                    Marker perioden som grønn når du har fått
                                    godkjent av alle tre
                                </strong>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    )
}
